import React, { useState } from 'react';
import { BarChart, LineChart, TrendingUp, BarChart3, Users } from 'lucide-react';
import { Bar, BarChart as RechartsBarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, LineChart as RechartsLineChart, Legend } from 'recharts';

const ActiveCampaigns = ({ onViewCampaign = () => {} }) => {
  const [selectedCampaign, setSelectedCampaign] = useState(1);

  // Sample data for campaigns
  const campaigns = [
    { 
      id: 1, 
      name: 'Summer Fuel Promotion', 
      startDate: '2023-06-01', 
      endDate: '2023-08-31', 
      targetSegments: ['Urban Premium', 'Highway Transit'],
      kpi: 'Volume increase',
      target: '+5%',
      current: '+5.3%',
      status: 'On track',
      volumeData: [
        { name: 'May', before: 2800, after: 2800 },
        { name: 'Jun', before: 2750, after: 2900 },
        { name: 'Jul', before: 2700, after: 2950 },
        { name: 'Aug', before: 2650, after: 2920 },
      ],
      marginData: [
        { name: 'May', before: 0.81, after: 0.81 },
        { name: 'Jun', before: 0.80, after: 0.82 },
        { name: 'Jul', before: 0.79, after: 0.83 },
        { name: 'Aug', before: 0.78, after: 0.84 },
      ],
      segmentImpact: [
        { name: 'Urban Premium', value: 6.2 },
        { name: 'Highway Transit', value: 4.8 },
        { name: 'Rural Standard', value: 3.1 },
        { name: 'Suburban Mixed', value: 4.2 },
      ]
    },
    { 
      id: 2, 
      name: 'Café Amazon Bundle', 
      startDate: '2023-07-15', 
      endDate: '2023-09-15', 
      targetSegments: ['Urban Premium', 'Suburban Mixed'],
      kpi: 'Cross-sell increase',
      target: '+10%',
      current: '+12.4%',
      status: 'Exceeding',
      volumeData: [
        { name: 'Jun', before: 2750, after: 2750 },
        { name: 'Jul', before: 2700, after: 2760 },
        { name: 'Aug', before: 2650, after: 2750 },
        { name: 'Sep (est)', before: 2600, after: 2730 },
      ],
      marginData: [
        { name: 'Jun', before: 0.80, after: 0.80 },
        { name: 'Jul', before: 0.79, after: 0.83 },
        { name: 'Aug', before: 0.78, after: 0.85 },
        { name: 'Sep (est)', before: 0.77, after: 0.84 },
      ],
      segmentImpact: [
        { name: 'Urban Premium', value: 8.7 },
        { name: 'Highway Transit', value: 2.1 },
        { name: 'Rural Standard', value: 1.8 },
        { name: 'Suburban Mixed', value: 9.3 },
      ]
    }
  ];
  
  const selectedCampaignData = campaigns.find(c => c.id === selectedCampaign);
  
  // Handle view details
  const handleViewDetails = () => {
    if (selectedCampaign) {
      onViewCampaign(selectedCampaign);
    }
  };
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Active Campaigns</h1>
        <p className="text-gray-600">Monitor and analyze your ongoing marketing campaigns.</p>
      </div>
      
      {/* Campaign Selector */}
      <div className="mb-6 flex space-x-4">
        {campaigns.map(campaign => (
          <button
            key={campaign.id}
            onClick={() => setSelectedCampaign(campaign.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              selectedCampaign === campaign.id 
                ? 'bg-purple-100 text-purple-700 border border-purple-200' 
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {campaign.name}
          </button>
        ))}
      </div>
      
      {/* Campaign Details */}
      {selectedCampaignData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Campaign Period</p>
              <h3 className="text-lg font-medium text-gray-900">
                {new Date(selectedCampaignData.startDate).toLocaleDateString()} - {new Date(selectedCampaignData.endDate).toLocaleDateString()}
              </h3>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Target Segments</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedCampaignData.targetSegments.map(segment => (
                  <span 
                    key={segment} 
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                  >
                    {segment}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Primary KPI</p>
              <h3 className="text-lg font-medium text-gray-900">{selectedCampaignData.kpi}</h3>
              <div className="flex items-center mt-1">
                <span className="text-sm text-gray-500 mr-2">Target: {selectedCampaignData.target}</span>
                <span className="text-sm font-medium text-green-600">Current: {selectedCampaignData.current}</span>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Status</p>
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-2 ${
                  selectedCampaignData.status === 'On track' ? 'bg-green-500' : 
                  selectedCampaignData.status === 'Exceeding' ? 'bg-purple-500' : 
                  selectedCampaignData.status === 'At risk' ? 'bg-yellow-500' : 'bg-red-500'
                }`} />
                <h3 className="text-lg font-medium text-gray-900">{selectedCampaignData.status}</h3>
              </div>
            </div>
          </div>
          
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Volume Impact Chart */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <BarChart3 size={20} className="mr-2 text-purple-600" />
                  Volume Impact
                </h3>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsBarChart
                    data={selectedCampaignData.volumeData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="before" name="Projected" fill="#9CA3AF" />
                    <Bar dataKey="after" name="Actual/Forecast" fill="#8B5CF6" />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Margin Impact Chart */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <TrendingUp size={20} className="mr-2 text-purple-600" />
                  Margin Impact (฿/L)
                </h3>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsLineChart
                    data={selectedCampaignData.marginData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="before" name="Projected" stroke="#9CA3AF" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="after" name="Actual/Forecast" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 4 }} />
                  </RechartsLineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* Segment Impact */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Users size={20} className="mr-2 text-purple-600" />
                Segment Impact Analysis
              </h3>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart
                  data={selectedCampaignData.segmentImpact}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 'dataMax + 2']} />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Impact (%)" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
      
      {/* Actions */}
      <div className="mt-6 flex justify-end space-x-4">
        <button className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50">
          Export Report
        </button>
        <button 
          className="px-4 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-800"
          onClick={handleViewDetails}
        >
          View Full Details
        </button>
      </div>
    </div>
  );
};

export default ActiveCampaigns;
